/**
 * Content Intelligence API service.
 */

import api from './api'
import type {
  CiWorkspace, CiContext, CiContextEntry, CiSource,
  CiCollectedPost, CiOutputChannel, CiGeneratedPost,
  CiWorkspaceStats, CiAiUsage,
} from '../types/contentIntelligence'

export const ciService = {
  // Workspaces
  getWorkspaces: () =>
    api.get<CiWorkspace[]>('/ci/workspaces').then(r => r.data),
  createWorkspace: (data: { name: string; type: string; timezone?: string }) =>
    api.post<CiWorkspace>('/ci/workspaces', data).then(r => r.data),
  getWorkspace: (id: number) =>
    api.get<CiWorkspace>(`/ci/workspaces/${id}`).then(r => r.data),
  updateWorkspace: (id: number, data: Partial<CiWorkspace>) =>
    api.patch<CiWorkspace>(`/ci/workspaces/${id}`, data).then(r => r.data),
  archiveWorkspace: (id: number) =>
    api.post(`/ci/workspaces/${id}/archive`).then(r => r.data),

  // L1 Context
  getContext: (wsId: number) =>
    api.get<CiContext | null>(`/ci/workspaces/${wsId}/context`).then(r => r.data),
  uploadContext: (wsId: number, raw_text: string) =>
    api.post<CiContext>(`/ci/workspaces/${wsId}/context`, { raw_text }).then(r => r.data),

  // L3 Entries
  getEntries: (wsId: number) =>
    api.get<CiContextEntry[]>(`/ci/workspaces/${wsId}/context/entries`).then(r => r.data),
  createEntry: (wsId: number, data: { entry_type: string; content: string; tags?: string }) =>
    api.post<CiContextEntry>(`/ci/workspaces/${wsId}/context/entries`, data).then(r => r.data),
  updateEntry: (wsId: number, entryId: number, data: Partial<CiContextEntry>) =>
    api.patch<CiContextEntry>(`/ci/workspaces/${wsId}/context/entries/${entryId}`, data).then(r => r.data),
  deleteEntry: (wsId: number, entryId: number) =>
    api.delete(`/ci/workspaces/${wsId}/context/entries/${entryId}`).then(r => r.data),

  // Sources
  getSources: () =>
    api.get<CiSource[]>('/ci/sources').then(r => r.data),
  createSource: (data: { name: string; platform: string; external_id?: string }) =>
    api.post<CiSource>('/ci/sources', data).then(r => r.data),
  updateSource: (id: number, data: Partial<CiSource>) =>
    api.patch<CiSource>(`/ci/sources/${id}`, data).then(r => r.data),
  deleteSource: (id: number) =>
    api.delete(`/ci/sources/${id}`).then(r => r.data),

  // Workspace sources
  getWorkspaceSources: (wsId: number) =>
    api.get<CiSource[]>(`/ci/workspaces/${wsId}/sources`).then(r => r.data),
  subscribeSource: (wsId: number, sourceId: number) =>
    api.post(`/ci/workspaces/${wsId}/sources/${sourceId}`).then(r => r.data),
  unsubscribeSource: (wsId: number, sourceId: number) =>
    api.delete(`/ci/workspaces/${wsId}/sources/${sourceId}`).then(r => r.data),

  // Collected posts
  getPosts: (wsId: number, params?: { status?: string; min_score?: number; limit?: number }) =>
    api.get<CiCollectedPost[]>(`/ci/workspaces/${wsId}/posts`, { params }).then(r => r.data),
  forceGenerate: (wsId: number, postId: number) =>
    api.post(`/ci/workspaces/${wsId}/posts/${postId}/force-generate`).then(r => r.data),

  // Output channels
  getChannels: (wsId: number) =>
    api.get<CiOutputChannel[]>(`/ci/workspaces/${wsId}/channels`).then(r => r.data),
  createChannel: (wsId: number, data: { platform: string; name: string; external_id?: string; lang?: string }) =>
    api.post<CiOutputChannel>(`/ci/workspaces/${wsId}/channels`, data).then(r => r.data),
  updateChannel: (wsId: number, chId: number, data: Partial<CiOutputChannel>) =>
    api.patch<CiOutputChannel>(`/ci/workspaces/${wsId}/channels/${chId}`, data).then(r => r.data),
  deleteChannel: (wsId: number, chId: number) =>
    api.delete(`/ci/workspaces/${wsId}/channels/${chId}`).then(r => r.data),

  // Staging
  getStaging: (wsId: number, status?: string) =>
    api.get<CiGeneratedPost[]>(`/ci/workspaces/${wsId}/staging`, { params: status ? { status } : undefined }).then(r => r.data),
  approvePost: (postId: number) =>
    api.patch(`/ci/staging/${postId}/approve`).then(r => r.data),
  rejectPost: (postId: number, reason?: string, user_comment?: string) =>
    api.patch(`/ci/staging/${postId}/reject`, { reason, user_comment }).then(r => r.data),
  editPost: (postId: number, text_edited: string, user_comment?: string) =>
    api.patch(`/ci/staging/${postId}/edit`, { text_edited, user_comment }).then(r => r.data),
  deferPost: (postId: number, deferred_until: string, comment?: string) =>
    api.patch(`/ci/staging/${postId}/defer`, { deferred_until, comment }).then(r => r.data),
  regeneratePost: (postId: number) =>
    api.post(`/ci/staging/${postId}/regenerate`).then(r => r.data),

  // Published
  getPublished: (wsId: number) =>
    api.get<CiGeneratedPost[]>(`/ci/workspaces/${wsId}/published`).then(r => r.data),

  // Stats & AI usage
  getStats: (wsId: number) =>
    api.get<CiWorkspaceStats>(`/ci/workspaces/${wsId}/stats`).then(r => r.data),
  getAiUsage: (wsId: number, period: string = 'today') =>
    api.get<CiAiUsage>(`/ci/workspaces/${wsId}/ai-usage`, { params: { period } }).then(r => r.data),

  // Planner
  getPlannerStatus: (wsId: number) =>
    api.get(`/ci/workspaces/${wsId}/planner-status`).then(r => r.data),
  runPlanner: (wsId: number) =>
    api.post(`/ci/workspaces/${wsId}/planner/run`).then(r => r.data),

  // Insights — Style Cloning, Topic Map, News Radar
  analyzeStyle: (wsId: number, posts_text: string) =>
    api.post(`/ci/insights/workspaces/${wsId}/style/analyze`, { posts_text }).then(r => r.data),
  getStyle: (wsId: number) =>
    api.get(`/ci/insights/workspaces/${wsId}/style`).then(r => r.data),
  buildTopicMap: (wsId: number) =>
    api.post(`/ci/insights/workspaces/${wsId}/topic-map/build`).then(r => r.data),
  getTopicMap: (wsId: number) =>
    api.get(`/ci/insights/workspaces/${wsId}/topic-map`).then(r => r.data),
  scanNews: (wsId: number) =>
    api.post(`/ci/insights/workspaces/${wsId}/news-radar/scan`).then(r => r.data),
}
